import { useQuery } from '@tanstack/react-query';

import { RequestConfig } from '~/common/services/requests';
import { handleError } from '../../../../utils/admin';
import { useSubscribeToDocumentStatusUpdated } from '../../../../websocketApi';
import { DEFAULT_HANDLERS } from '../../../constants/defaultHandlers';
import { DocumentParams } from '../../../documents/types/DocumentParams';
import { useRequestConstructor } from '../../../hooks/useRequestConstructor';
import { getApiErrorMessage } from '../../../logic';
import { unwrapNonPaginatedResponseDataAs } from '../../../logic/unwrapNonPaginatedResponseDataAs';
import { ApiErrorResponse } from '../../../types';
import { WORKFLOWS_BASE_PATH } from '../../constants/workflowsBasePath';
import { getWorkflow } from '../../requests/getWorkflow';

export const useWorkflow = (
  documentParams: DocumentParams,
  documentId: string,
  workflowName: string | undefined,
  params?: RequestConfig['params'],
) => {
  const { constructGet } = useRequestConstructor();

  const { data, refetch, ...rest } = useQuery<
    Awaited<ReturnType<typeof getWorkflow>>
  >({
    queryKey: [
      WORKFLOWS_BASE_PATH,
      documentId,
      documentParams,
      workflowName,
      params,
    ],
    queryFn: () =>
      getWorkflow(
        constructGet,
        documentParams,
        documentId,
        workflowName,
        params,
        { preventDefaultErrorToast: true },
      ),
    onError: (error: ApiErrorResponse) => {
      const status = error?.response?.status ?? -1;
      if (status < 400 || status >= 500) {
        handleError(getApiErrorMessage(error));
      }
    },
    keepPreviousData: true,
    enabled: Boolean(workflowName),
    retry: DEFAULT_HANDLERS.noRetryFor400s,
  });

  useSubscribeToDocumentStatusUpdated(documentId, () => {
    // refetch bypasses the cache and gets the latest data
    // this is preferable to invalidate + wait for next fetch
    if (workflowName) {
      refetch();
    }
  });

  return {
    ...unwrapNonPaginatedResponseDataAs(data, refetch, 'workflow'),
    ...rest,
  };
};
